import { useEffect, useState } from 'react';
import { onSnapshot, type DocumentReference, type Query } from 'firebase/firestore';

type State<T> = { rows: Array<T & { id: string }>; loading: boolean; error: Error | null };

/**
 * A live list. Three states, not two: "loading" is distinct from "empty",
 * because a recruiter looking at an empty queue needs to know whether there is
 * genuinely nothing to do or whether the read has not landed yet.
 *
 * An error is surfaced rather than swallowed — a rules refusal that renders as
 * an empty list is the single most confusing failure this app can produce.
 *
 * `deps` is the caller's re-subscribe key. The query object itself is rebuilt
 * on every render and would re-subscribe forever if used as the dependency.
 */
export function useDocs<T>(query: Query | null, deps: unknown[]): State<T> {
  const [state, setState] = useState<State<T>>({ rows: [], loading: query !== null, error: null });

  useEffect(() => {
    if (!query) {
      setState({ rows: [], loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    return onSnapshot(
      query,
      (snap) => {
        setState({
          rows: snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) })),
          loading: false,
          error: null,
        });
      },
      (error: Error) => setState({ rows: [], loading: false, error })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

type DocState<T> = { row: (T & { id: string }) | null; loading: boolean; error: Error | null };

export function useDocRow<T>(ref: DocumentReference | null, deps: unknown[]): DocState<T> {
  const [state, setState] = useState<DocState<T>>({ row: null, loading: ref !== null, error: null });

  useEffect(() => {
    if (!ref) {
      setState({ row: null, loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    return onSnapshot(
      ref,
      (snap) => {
        setState({
          row: snap.exists() ? ({ id: snap.id, ...(snap.data() as T) }) : null,
          loading: false,
          error: null,
        });
      },
      (error: Error) => setState({ row: null, loading: false, error })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}
